import { useEffect, useMemo, useState } from 'react';
import { Close } from '@mui/icons-material';
import { Box, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { slice } from 'lodash';
import dayjs from 'dayjs';
import { Plus } from 'react-feather';

// Components
import { renderNumberOrDefault, renderTextOrDefault } from 'src/utils/helpers/stringHelpers';
import { FORMAT_DATE_TIME, LIMIT_GET_LIST } from 'src/utils/constants/config';
import Table from 'src/components/Table';

// Utilities
import { ICountryTrade, IPartnerTradeTransit } from 'src/utils/typings/response';
import { useQueryDataCountryTrade } from 'src/services/queries';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import IconFeather from 'src/components/common/Icon';
import { KeyFilterChartTeuPort } from 'src/utils/constants/enum';
import BarChart from 'src/components/HighChart/Barchart';

interface IProps {
  locode: string;
  partner: IPartnerTradeTransit;
  isExport: number;
}

const TransitTradeChart = ({ locode, partner, isExport }: IProps) => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(LIMIT_GET_LIST);
  const [listCountryTrade, setListCountryTrade] = useState<Array<ICountryTrade>>([]);
  const [showTableFreightRate, setShowFreightRate] = useState(false);
  const { data, isFetching } = useQueryDataCountryTrade({
    locode,
    locodePartner: partner?.value || '',
    isExport: isExport,
    freq: KeyFilterChartTeuPort.month,
  });

  const allDataCountryTrade = getArrayDefault(data?.data);

  useEffect(() => {
    const initialList = slice(getArrayDefault(allDataCountryTrade), page * pageSize, (page + 1) * pageSize);
    setListCountryTrade(initialList);
  }, [allDataCountryTrade, page]);

  const lineChartYData = useMemo(() => {
    return allDataCountryTrade.map((item: ICountryTrade) => [item.volume || 0]);
  }, [allDataCountryTrade]);

  const lineChartXData = useMemo(() => {
    const res = allDataCountryTrade.map((item: ICountryTrade) => {
      return dayjs(item?.date, FORMAT_DATE_TIME.yyyy_mm_dd).format(FORMAT_DATE_TIME.FULL_ONLY_DATE);
    });
    return res;
  }, [allDataCountryTrade]);

  const configColumn: ConfigCell<ICountryTrade>[] = [
    {
      id: 'date',
      disablePadding: true,
      disableSort: true,
      label: 'Date',
      cell: (row: ICountryTrade) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderTextOrDefault(row?.date)}
          </Typography>
        );
      },
    },
    {
      id: 'volume',
      disablePadding: true,
      disableSort: true,
      label: `${locode} > ${renderTextOrDefault(partner?.value)}`,
      cell: (row: ICountryTrade) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderNumberOrDefault(row?.volume)}
          </Typography>
        );
      },
    },
  ];

  return (
    <Box p={2} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: theme.shape.borderRadius / 2 }}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h5" align="left">
          Container trade - {locode} &gt; {renderTextOrDefault(partner?.value)} (TEU)
        </Typography>
        <IconButton onClick={() => setShowFreightRate(!showTableFreightRate)} color="primary">
          {showTableFreightRate ? <Close /> : <IconFeather icon={<Plus />} />}
        </IconButton>
      </Stack>
      <Box mt={3} />
      {showTableFreightRate ? (
        <Table
          data={listCountryTrade}
          configColumn={configColumn}
          dataKey="date"
          onChangePage={(e) => setPage(e)}
          onChangePageSize={(e) => setPageSize(e)}
          page={page}
          pageSize={pageSize}
          loading={isFetching}
          maxRow={5}
          totalRecords={getArrayDefault(allDataCountryTrade).length}
          sxTableContainer={{
            border: `1px solid ${theme.palette.divider}`,
          }}
        />
      ) : (
        <BarChart
          loading={isFetching}
          tooltipFormatter={function () {
            return `<b style="color: ${theme.palette.text.secondary}"></b><b>${this.point.category}</b>
<br/><b style="color: ${theme.palette.text.secondary}">Sea Shipments </b><b>${this.y}</b>`;
          }}
          yAxis={{
            min: 0,
            stackLabels: {
              enabled: false,
            },
          }}
          xAxis={{
            allowDecimals: false,
            labels: {
              formatter: function () {
                const year = String(this.value).split('/')[2]; // Extract year from categories
                return year;
              },
            },
            tickInterval: 12, // Show one tick per year (12 months per year or 52 weeks per year)
            categories: lineChartXData,
          }}
          legend={{
            align: 'center',
            verticalAlign: 'bottom',
            backgroundColor: 'white',
            borderColor: 'transparent',
            borderWidth: 1,
            shadow: false,
          }}
          plotOptions={{
            column: {
              stacking: 'normal',
            },
          }}
          series={[
            {
              name: 'China',
              type: 'column',
              data: lineChartYData,
              color: 'rgba(83, 136, 216, 0.60)',
              borderWidth: 0,
            },
          ]}
          categories={lineChartXData}
          height={500}
        />
      )}
    </Box>
  );
};

export default TransitTradeChart;
